import React, { useState, useEffect } from 'react';
import { useMiniERPEDI } from './hooks/useMiniERPEDI';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faClock, faRupeeSign } from '@fortawesome/free-solid-svg-icons';

const emptyForm = {
  travelMin: '', waitingMin: '', discussionMin: '', returnMin: '',
  travelMode: 'Bike', travelCost: '', parkingCost: '', foodCost: '',
};

const MiniERPTrasCostTime = ({ caseId }) => {
  const { createTrasCostTime, fetchTrasCostTimeSummary } = useMiniERPEDI();
  const { status, showSuccess, showError } = useStatusBanner();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ totalMinutes: 0, totalCost: 0, visitCount: 0 });
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const res = await fetchTrasCostTimeSummary(caseId);
    setEntries(res?.data?.entries || []);
    setSummary(res?.data?.summary || { totalMinutes: 0, totalCost: 0, visitCount: 0 });
  };

  useEffect(() => { if (caseId) load(); }, [caseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, k === 'travelMode' ? v : Number(v) || 0]));
      await createTrasCostTime(caseId, payload);
      showSuccess('Cost & time logged.');
      setForm(emptyForm);
      await load();
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold text-cyan-200 mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faClock} /> Cost &amp; Time
      </h2>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-gray-800 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-cyan-300">{summary.totalMinutes}</div>
          <div className="text-gray-500 text-xs">Total Minutes</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-rose-400">₹{summary.totalCost}</div>
          <div className="text-gray-500 text-xs">Total Expense</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-white">{summary.visitCount}</div>
          <div className="text-gray-500 text-xs">Entries Logged</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-2xl p-4 space-y-3 mb-5">
        <p className="text-gray-400 text-xs">Time (minutes)</p>
        <div className="grid grid-cols-4 gap-2">
          <input type="number" placeholder="Travel" value={form.travelMin} onChange={(e) => setForm({ ...form, travelMin: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm" />
          <input type="number" placeholder="Waiting" value={form.waitingMin} onChange={(e) => setForm({ ...form, waitingMin: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm" />
          <input type="number" placeholder="Discussion" value={form.discussionMin} onChange={(e) => setForm({ ...form, discussionMin: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm" />
          <input type="number" placeholder="Return" value={form.returnMin} onChange={(e) => setForm({ ...form, returnMin: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm" />
        </div>

        <p className="text-gray-400 text-xs pt-2">Expense</p>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.travelMode} onChange={(e) => setForm({ ...form, travelMode: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm">
            <option>Bike</option><option>Auto</option><option>Taxi</option><option>Train / Bus</option>
          </select>
          <input type="number" placeholder="Travel Cost ₹" value={form.travelCost} onChange={(e) => setForm({ ...form, travelCost: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm" />
          <input type="number" placeholder="Parking ₹" value={form.parkingCost} onChange={(e) => setForm({ ...form, parkingCost: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm" />
          <input type="number" placeholder="Food ₹" value={form.foodCost} onChange={(e) => setForm({ ...form, foodCost: e.target.value })}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm" />
        </div>

        <StatusBanner status={status} />
        <button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-500 rounded-xl py-2.5 font-medium flex items-center justify-center gap-2">
          <FontAwesomeIcon icon={faPlus} /> Log Entry
        </button>
      </form>

      <h3 className="text-sm font-semibold text-gray-300 mb-2">Entries</h3>
      <div className="space-y-2">
        {entries.length === 0 && <p className="text-gray-500 text-sm">No cost/time entries yet.</p>}
        {entries.map((e) => (
          <div key={e._id} className="bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 flex justify-between items-center text-sm">
            <div className="text-gray-300">{new Date(e.date).toLocaleDateString()} • {e.travelMode}</div>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-cyan-300">{(e.travelMin || 0) + (e.waitingMin || 0) + (e.discussionMin || 0) + (e.returnMin || 0)} min</span>
              <span className="text-rose-400 flex items-center gap-1"><FontAwesomeIcon icon={faRupeeSign} />{(e.travelCost || 0) + (e.parkingCost || 0) + (e.foodCost || 0)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MiniERPTrasCostTime;
